import { useEffect, useState } from "react";
import { Loader2, Printer } from "lucide-react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { addDaysISO, fmtNum, fmtQty, todayISO } from "./kitchenUtils";

export const ShoppingTab = () => {
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(addDaysISO(todayISO(), 6));
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    api.get("/admin/kitchen/shopping", { params: { start, end } })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData({ items: [], missing_recipes: [], order_count: 0, est_total: 0 }));
    return () => { alive = false; };
  }, [start, end]);

  return (
    <div className="space-y-3" data-testid="shopping-tab">
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="shopping-start" className="h-11 w-44 rounded-full bg-white border-black/10" />
        <span className="text-brand-charcoal/50">to</span>
        <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="shopping-end" className="h-11 w-44 rounded-full bg-white border-black/10" />
        <button type="button" onClick={() => window.print()} data-testid="shopping-print" className="h-11 px-4 rounded-full bg-brand-charcoal text-white text-[11.5px] font-bold uppercase tracking-[0.1em] inline-flex items-center gap-2">
          <Printer className="h-4 w-4" /> Print list
        </button>
      </div>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-brand-teal" /> : (
        <>
          <p className="text-[13px] text-brand-charcoal/70" data-testid="shopping-summary">
            Shopping list for {start} → {end} · {data.order_count} order{data.order_count === 1 ? "" : "s"}
            {data.est_total > 0 && <> · est. <b>${data.est_total.toFixed(2)}</b></>}
          </p>
          {data.missing_recipes.length > 0 && (
            <p className="rounded-xl bg-brand-gold/20 px-3 py-2 text-[12px] text-brand-charcoal" data-testid="shopping-missing">
              Not counted (no recipe yet): {data.missing_recipes.join(", ")}
            </p>
          )}
          <table className="w-full text-[13px] bg-white rounded-xl overflow-hidden border border-black/10" data-testid="shopping-table">
            <thead className="bg-brand-charcoal text-white text-left text-[11px] uppercase tracking-[0.08em]">
              <tr><th className="p-2.5">Ingredient</th><th className="p-2.5">Need</th><th className="p-2.5">Buy</th><th className="p-2.5 hidden sm:table-cell">Vendor</th><th className="p-2.5 text-right">Est.</th></tr>
            </thead>
            <tbody>
              {data.items.map((x) => (
                <tr key={x.ingredient_id} className="border-t border-black/5" data-testid={`shopping-row-${x.ingredient_id}`}>
                  <td className="p-2.5"><b>{x.name}</b><span className="block text-[11px] text-brand-charcoal/50">{x.category}</span></td>
                  <td className="p-2.5 tabular-nums">{fmtQty(x.total, x.unit, x.count_label)}</td>
                  <td className="p-2.5 tabular-nums">{x.packs != null ? `${x.packs} ${x.pack_label}${x.packs === 1 ? "" : "s"} (${fmtNum(x.pack_qty)} ea)` : "—"}</td>
                  <td className="p-2.5 hidden sm:table-cell">{x.vendor || "—"}</td>
                  <td className="p-2.5 text-right tabular-nums">{x.est_cost != null ? `$${x.est_cost.toFixed(2)}` : "—"}</td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-brand-charcoal/50">Nothing to buy for these dates.</td></tr>}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default ShoppingTab;
